import { useState } from "react";
import { searchByName, type BusinessLabels, type SessionExtraCandidate } from "@teamna/shared";
import { useAddSessionExtra, useSessionExtraSearch } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Button, Field, Sheet, showToast } from "../components/index.ts";
import { CandidateRow, NotBookedList } from "./not-booked.tsx";

/** The API's limits for a name and a phone (`coach-participants.ts` schemas). */
const NAME_MAX = 120;
const PHONE_MAX = 40;

/**
 * "Someone else came" (strategy repo: teamna/prototypes/someone-else-came.html). The sheet opens on
 * the level's Did not book list (2026-10-19): most who come unbooked are there. For anyone else the
 * coach searches the club; nothing found, the coach types the name. A swimmer of the club is marked
 * Came today; a name that waits for a parent joins the session. The coach sees names, levels and
 * coaches only. Came takes 1 session from the family's package (2026-10-29), or an owed one when none is left.
 */
export function SomeoneElseSheet({
  open,
  onClose,
  businessId,
  sessionId,
  sessionTitle,
  labels,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  sessionId: string;
  /** "Learn to swim 18:00". */
  sessionTitle: string;
  labels: BusinessLabels;
}) {
  const [q, setQ] = useState("");
  const [step, setStep] = useState<"search" | "new">("search");
  const [name, setName] = useState("");
  const [note, setNote] = useState(false);
  const [parent, setParent] = useState("");
  const [phone, setPhone] = useState("");
  const search = useSessionExtraSearch(open ? businessId : null, sessionId, q);
  const add = useAddSessionExtra(businessId, sessionId);
  const one = labels.participantSingular;
  const typed = q.trim();

  const close = () => {
    setQ("");
    setStep("search");
    setName("");
    setNote(false);
    setParent("");
    setPhone("");
    onClose();
  };
  const done = (who: string, text: string) => {
    showToast(text.replace("{name}", who.split(" ")[0] ?? who));
    close();
  };
  const fail = (error: unknown) => showToast(messageForError(error), { tone: "info" });
  const came = (item: SessionExtraCandidate, from: "not_booked" | "search") => (
    <Button
      size="sm"
      loading={add.isPending}
      onClick={() =>
        add.mutate(item.kind === "participant" ? { kind: "participant", participantId: item.id, from } : { kind: "waiting", pendingParticipantId: item.id, from }, {
          onSuccess: (answer) => done(answer.name, "{name} is marked Came."),
          onError: fail,
        })
      }
    >
      Came today
    </Button>
  );

  // Only the answer for the text in the field now, and never while a new one loads.
  const current = typed.length >= 2 && !search.isFetching && !search.isPlaceholderData ? search.data : undefined;
  const items = current?.items ?? [];
  const inSession = current?.inSession ?? [];
  // The band is worked out here: the API sends names only.
  useTrackNameSearch("someone_else", current ? q : "", searchByName(items, q, (item) => [item.name]).results, items.length);

  return (
    <Sheet
      open={open}
      onClose={close}
      title={step === "search" ? "Someone else came" : `A new ${one}`}
      subtitle={step === "search" ? `${sessionTitle}. Pick from who did not book, or search the club.` : `${sessionTitle}.`}
      actions={
        step === "new" ? (
          <Button
            fullWidth
            disabled={!name.trim()}
            loading={add.isPending}
            onClick={() =>
              add.mutate(
                { kind: "new", name: name.trim(), noteParentName: parent.trim() || null, notePhone: phone.trim() || null },
                { onSuccess: (answer) => done(answer.name, "{name} is added as Came."), onError: fail },
              )
            }
          >
            Add {name.trim().split(" ")[0] || one}, came today
          </Button>
        ) : undefined
      }
    >
      {step === "search" ? (
        <div className="flex flex-col gap-3">
          {typed.length < 2 ? (
            <section aria-label="Did not book" className="flex flex-col gap-2">
              <p className="eyebrow text-ink-2">Did not book</p>
              <NotBookedList businessId={businessId} sessionId={sessionId} labels={labels} openClassNext="Search the club for a name below." action={(item) => came(item, "not_booked")} />
            </section>
          ) : null}
          <Field
            label="Not on the list? Search the club"
            placeholder="Type a name"
            autoComplete="off"
            value={q}
            maxLength={NAME_MAX}
            onChange={(e) => setQ(e.target.value)}
          />
          {inSession.length ? (
            <p className="notice notice-warning rounded-card px-3 py-2 text-[13.5px]">
              <b>{inSession.join(", ")}</b> {inSession.length > 1 ? "are" : "is"} already in this session.
            </p>
          ) : null}
          {items.map((item) => (
            <CandidateRow key={`${item.kind}-${item.id}`} name={item.name} detail={item.where} action={came(item, "search")} />
          ))}
          {typed.length >= 2 && search.error && !search.isFetching ? (
            <div className="notice notice-warning flex flex-col gap-2 rounded-card px-3 py-2.5 text-[13.5px]">
              <span>The search did not work. Try again before you add a new {one}.</span>
              <Button variant="secondary" onClick={() => void search.refetch()}>
                Try again
              </Button>
            </div>
          ) : null}
          {current ? (
            <div className="notice notice-info flex flex-col gap-2 rounded-card px-3 py-2.5 text-[13.5px]">
              <span>{items.length ? `Not ${items.length > 1 ? "one of them" : (items[0]!.name.split(" ")[0] ?? "this one")}?` : `No ${one} called "${typed}" here.`}</span>
              <Button
                variant="secondary"
                onClick={() => {
                  setName(typed);
                  setStep("new");
                }}
              >
                Add "{typed}" as a new {one}
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Field label={`${one.charAt(0).toUpperCase()}${one.slice(1)} name`} value={name} autoComplete="off" maxLength={NAME_MAX} onChange={(e) => setName(e.target.value)} />
          <p className="text-[13px] text-ink-2">
            {name.trim().split(" ")[0] || `The ${one}`} shows as Came. The owner links a {labels.memberSingular}.
          </p>
          <button type="button" className="self-start text-[13px] font-semibold text-accent-strong" aria-expanded={note} onClick={() => setNote(!note)}>
            {note ? "Hide the note" : "Add a note for the owner (optional)"}
          </button>
          {note ? (
            <>
              <Field label={`${labels.memberSingular.charAt(0).toUpperCase()}${labels.memberSingular.slice(1)} name`} value={parent} placeholder="Optional" maxLength={NAME_MAX} onChange={(e) => setParent(e.target.value)} />
              <Field label={`${labels.memberSingular.charAt(0).toUpperCase()}${labels.memberSingular.slice(1)} mobile`} value={phone} placeholder="Optional" inputMode="tel" maxLength={PHONE_MAX} onChange={(e) => setPhone(e.target.value)} />
              <p className="text-[12.5px] text-ink-2">Only the owner sees the note. Teamna sends nothing to this number.</p>
            </>
          ) : null}
          <Button variant="ghost" onClick={() => setStep("search")}>
            Back to the search
          </Button>
        </div>
      )}
    </Sheet>
  );
}
